import type { ApiEndpoint } from "@/shared/api-endpoint";
import { isObject, isStringArray } from "@/shared/utils/typeGuards";

export const kSpliceSearchRoute = "/splice-search";

export enum SpliceAssetCategory {
    All = "all",
    Loop = "loop",
    OneShot = "one-shot",
}

const kAssetCategoryParams: Record<SpliceAssetCategory, string | undefined> = {
    [SpliceAssetCategory.All]: undefined,
    [SpliceAssetCategory.Loop]: "ASSET_CATEGORY_LOOP",
    [SpliceAssetCategory.OneShot]: "ASSET_CATEGORY_ONE_SHOT",
};

export const toAssetCategoryParam = (category: SpliceAssetCategory): string | undefined =>
    kAssetCategoryParams[category];

const kKeyToAtlantis: Record<string, string> = {
    c: "KEY_TYPE_C",
    "c#": "KEY_TYPE_C_SHARP",
    d: "KEY_TYPE_D",
    "d#": "KEY_TYPE_D_SHARP",
    e: "KEY_TYPE_E",
    f: "KEY_TYPE_F",
    "f#": "KEY_TYPE_F_SHARP",
    g: "KEY_TYPE_G",
    "g#": "KEY_TYPE_G_SHARP",
    a: "KEY_TYPE_A",
    "a#": "KEY_TYPE_A_SHARP",
    b: "KEY_TYPE_B",
};

export const toSpliceSearchKeyParam = (key: string): string | undefined =>
    key !== "" ? kKeyToAtlantis[key.toLowerCase()] : undefined;

const kChordTypeToAtlantis: Record<string, string> = {
    major: "CHORD_TYPE_MAJOR",
    minor: "CHORD_TYPE_MINOR",
};

export const toSpliceSearchChordTypeParam = (chordType: string): string | undefined =>
    chordType !== "" ? kChordTypeToAtlantis[chordType.toLowerCase()] : undefined;

export enum SpliceSortType {
    Relevance = "SORT_TYPE_RELEVANCE",
    Popularity = "SORT_TYPE_POPULARITY",
    Recency = "SORT_TYPE_RECENCY",
    Random = "SORT_TYPE_RANDOM",
}

export enum SpliceSortOrder {
    Desc = "SORT_ORDER_DESC",
    Asc = "SORT_ORDER_ASC",
}

export interface SpliceSearchRequestBody {
    readonly query: string;
    readonly count: number;
    readonly page: number;
    readonly semanticWeight?: number;
    readonly assetCategory?: string;
    readonly queryTags?: readonly string[];
    readonly key?: string;
    readonly chordType?: string;
    readonly bpmMin?: number;
    readonly bpmMax?: number;
    readonly sort?: string;
    readonly order?: string;
    readonly bpm?: number;
    /**
     * When set, the search switches to the CoSo (Content-based Similarity)
     * path: the referenced audio file is uploaded to Atlantis as `seedAudioFile`
     * and its embedding is used instead of tag/semantic matching.
     */
    readonly seedAudioFileKey?: string;
    /**
     * When true, the query text is not forwarded to Atlantis as `querySemantic`.
     * Useful for testing tag/filter-only search without semantic vector matching.
     */
    readonly excludeQuerySemantic?: boolean;
}

export const isSpliceSearchRequestBody = (value: unknown): value is SpliceSearchRequestBody =>
    isObject(value) &&
    typeof value.query === "string" &&
    typeof value.count === "number" &&
    typeof value.page === "number" &&
    (value.semanticWeight === undefined || typeof value.semanticWeight === "number") &&
    (value.assetCategory === undefined || typeof value.assetCategory === "string") &&
    (value.queryTags === undefined || isStringArray(value.queryTags)) &&
    (value.key === undefined || typeof value.key === "string") &&
    (value.chordType === undefined || typeof value.chordType === "string") &&
    (value.bpmMin === undefined || typeof value.bpmMin === "number") &&
    (value.bpmMax === undefined || typeof value.bpmMax === "number") &&
    (value.sort === undefined || typeof value.sort === "string") &&
    (value.order === undefined || typeof value.order === "string") &&
    (value.bpm === undefined || typeof value.bpm === "number") &&
    (value.seedAudioFileKey === undefined || typeof value.seedAudioFileKey === "string") &&
    (value.excludeQuerySemantic === undefined || typeof value.excludeQuerySemantic === "boolean");

export interface SpliceSearchResultMetadata {
    readonly uuid: string;
    readonly name: string;
    readonly bpm: number;
    readonly key: string;
    readonly chordType: string;
    readonly duration: number;
    readonly assetCategory: string;
    readonly tags: readonly string[];
    readonly provider: string;
    readonly packName: string;
    readonly packArtworkUrl: string;
}

export interface SpliceSearchResult {
    readonly metadata: SpliceSearchResultMetadata;
    readonly previewUrl: string;
}

export interface SpliceSearchSuccessResponse {
    readonly ok: true;
    readonly results: readonly SpliceSearchResult[];
    readonly tagSummary: readonly string[];
}

const isSpliceSearchResultMetadata = (value: unknown): value is SpliceSearchResultMetadata =>
    isObject(value) &&
    typeof value.uuid === "string" &&
    typeof value.name === "string" &&
    typeof value.bpm === "number" &&
    typeof value.key === "string" &&
    typeof value.chordType === "string" &&
    typeof value.duration === "number" &&
    typeof value.assetCategory === "string" &&
    isStringArray(value.tags) &&
    typeof value.provider === "string" &&
    typeof value.packName === "string" &&
    typeof value.packArtworkUrl === "string";

export const isSpliceSearchResult = (value: unknown): value is SpliceSearchResult =>
    isObject(value) &&
    isSpliceSearchResultMetadata(value.metadata) &&
    typeof value.previewUrl === "string";

export const isSpliceSearchSuccessResponse = (
    value: unknown,
): value is SpliceSearchSuccessResponse =>
    isObject(value) &&
    value.ok === true &&
    Array.isArray(value.results) &&
    value.results.every(isSpliceSearchResult) &&
    isStringArray(value.tagSummary);

export const kSpliceSearchEndpoint: ApiEndpoint<
    SpliceSearchRequestBody,
    SpliceSearchSuccessResponse
> = {
    route: kSpliceSearchRoute,
    label: "Splice Search",
    source: "Atlantis",
    category: "Semantic Intent",
    description: "Searches the Splice sounds catalog using text or semantic-intent queries.",
    isRequestBody: isSpliceSearchRequestBody,
    isSuccessResponse: isSpliceSearchSuccessResponse,
};
